<template>
  <div class="pellicule" :style="overlayBgStyle">
    <div class="closeButtons">
      <q-btn icon="close" flat round dense @click="$emit('closePellicule')" aria-label="Fermer" />
      <ReturnHomeButton @click="$emit('closeAllDialogs')" />
    </div>

    <main class="film-stage">
      <div v-if="loading" class="state-panel">Chargement...</div>
      <div v-else-if="resources.length === 0" class="state-panel">Aucune ressource à afficher</div>

      <template v-else>
        <button class="nav-button nav-button--left" type="button" @click="scrollTrack(-1)" aria-label="Précédent">
          <q-icon name="chevron_left" />
        </button>

        <div ref="trackRef" class="film-track">
          <article
            v-for="(resource, index) in resources"
            :key="resource.resource_id ?? index"
            class="film-card"
            :style="{ animationDelay: `${Math.min(index * 40, 400)}ms` }"
            @click="openLightbox(index)"
          >
            <img
              v-if="getResourceType(resource) === 'image' && resource.url"
              :src="resource.url"
              :alt="resource.title || 'Ressource'"
              loading="lazy"
            />
            <video
              v-else-if="getResourceType(resource) === 'video' && resource.url"
              :src="resource.url"
              muted
              playsinline
            />
            <div v-else class="file-card">
              <q-icon :name="fileIcon(resource)" />
              <span>{{ resource.url === null ? 'Média indisponible' : (resource.title || 'Chargement...') }}</span>
            </div>
            <div class="card-caption">
              <span>{{ String(index + 1).padStart(2, '0') }}</span>
              <strong v-if="resource.title">{{ resource.title }}</strong>
            </div>
          </article>
        </div>

        <button class="nav-button nav-button--right" type="button" @click="scrollTrack(1)" aria-label="Suivant">
          <q-icon name="chevron_right" />
        </button>
      </template>
    </main>

    <transition name="fade">
      <div v-if="activeLightboxIndex !== null" class="lightbox" @click="closeLightbox">
        <div class="lightbox-content" @click.stop>
          <button class="close-button" type="button" @click="closeLightbox" aria-label="Fermer l'aperçu">
            <q-icon name="close" />
          </button>
          <button
            class="lightbox-nav lightbox-nav--prev"
            type="button"
            @click="navigateLightbox(-1)"
            aria-label="Précédent"
          >
            <q-icon name="chevron_left" />
          </button>
          <img
            v-if="getResourceType(activeResource) === 'image' && activeUrl"
            :src="activeUrl"
            :alt="activeResource.title || 'Ressource'"
          />
          <video
            v-else-if="getResourceType(activeResource) === 'video' && activeUrl"
            :src="activeUrl"
            controls
            autoplay
            playsinline
          />
          <div v-else class="file-card file-card--large">
            <q-icon :name="fileIcon(activeResource)" />
            <span>{{ activeUrl === null ? 'Média indisponible' : (activeResource.title || 'Chargement...') }}</span>
          </div>
          <button
            class="lightbox-nav lightbox-nav--next"
            type="button"
            @click="navigateLightbox(1)"
            aria-label="Suivant"
          >
            <q-icon name="chevron_right" />
          </button>
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup>
import axios from 'axios';
import { computed, defineEmits, defineProps, onMounted, ref } from 'vue';
import ReturnHomeButton from './ReturnHomeButton.vue';
import { useOverlayBg } from '../composables/useOverlayBg';

const props = defineProps(['blockDatas']);
defineEmits(['closePellicule', 'closeAllDialogs']);

const { overlayBgStyle } = useOverlayBg();

const resources = ref([]);
const loading = ref(true);
const trackRef = ref(null);
const activeLightboxIndex = ref(null);

const activeResource = computed(() => resources.value[activeLightboxIndex.value] || {});
const activeUrl = computed(() => activeResource.value.url ?? '');

const getResourceType = (resource) => {
  const name = resource?.name || '';
  if (/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(name)) return 'image';
  if (/\.(mp4|avi|mov|wmv|webm|mkv)$/i.test(name)) return 'video';
  if (/\.(mp3|wav|ogg|m4a)$/i.test(name)) return 'audio';
  if (/\.(pdf|doc|docx)$/i.test(name)) return 'doc';
  return 'file';
};

const fileIcon = (resource) => {
  const type = getResourceType(resource);
  if (type === 'audio') return 'audiotrack';
  if (type === 'doc') return 'description';
  return 'insert_drive_file';
};

const scrollTrack = (direction) => {
  if (!trackRef.value) return;
  const amount = Math.max(trackRef.value.clientWidth * 0.8, 320);
  trackRef.value.scrollBy({ left: direction * amount, behavior: 'smooth' });
};

const openLightbox = (index) => {
  activeLightboxIndex.value = index;
};

const closeLightbox = () => {
  activeLightboxIndex.value = null;
};

const navigateLightbox = (direction) => {
  if (!resources.value.length || activeLightboxIndex.value === null) return;
  activeLightboxIndex.value =
    (activeLightboxIndex.value + direction + resources.value.length) % resources.value.length;
};

const fetchResources = async () => {
  const associatedResources = props.blockDatas?.associatedResources || [];
  loading.value = true;

  const fetched = await Promise.all(
    associatedResources.map(async (resource, position) => {
      try {
        const response = await axios.get(`${process.env.VUE_APP_BASEURL}/s3Resource/${resource.name}`);
        return {
          ...resource,
          url: response.data,
          sortIndex: Number.isFinite(Number(resource.index)) ? Number(resource.index) : position,
        };
      } catch (error) {
        return {
          ...resource,
          url: null,
          sortIndex: Number.isFinite(Number(resource.index)) ? Number(resource.index) : position,
        };
      }
    })
  );

  fetched.sort((a, b) => a.sortIndex - b.sortIndex);
  resources.value = fetched;
  loading.value = false;
};

onMounted(fetchResources);
</script>

<style scoped>
.pellicule {
  background-color: rgba(217, 217, 217, 1);
  position: fixed;
  inset: 0;
  z-index: 9999;
  width: 100%;
  height: 100%;
  overflow: hidden;
  animation: overlayFadeIn 200ms ease both;
}

.closeButtons {
  position: absolute;
  top: 16px;
  right: 16px;
  width: auto;
  margin: 0;
  display: flex;
  gap: 10px;
  z-index: 10001;
}

.film-stage {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 86px 0 42px;
}

.film-track {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
  height: min(640px, 68vh);
  overflow-x: auto;
  overflow-y: hidden;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding: 0 max(64px, calc((100vw - 1420px) / 2)) 12px;
}

.film-track::-webkit-scrollbar {
  display: none;
}

.film-card {
  position: relative;
  flex: 0 0 auto;
  height: 100%;
  overflow: hidden;
  border-radius: 8px;
  background: #161616;
  cursor: pointer;
  scroll-snap-align: center;
  animation: cardIn 520ms ease both;
  transition: transform 180ms ease, opacity 180ms ease;
}

.film-card:hover {
  transform: translateY(-4px);
}

.film-card:active {
  transform: scale(0.985);
}

.film-card img,
.film-card video {
  width: auto;
  height: 100%;
  max-width: 82vw;
  display: block;
  object-fit: contain;
  transition: transform 560ms ease, filter 180ms ease;
}

.film-card:hover img,
.film-card:hover video {
  transform: scale(1.035);
  filter: saturate(1.08);
}

.card-caption {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  padding: 48px 18px 16px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.76), transparent);
  opacity: 0;
  transform: translateY(8px);
  transition: opacity 160ms ease, transform 160ms ease;
}

.film-card:hover .card-caption {
  opacity: 1;
  transform: translateY(0);
}

.card-caption span {
  color: #d7d7d7;
  font-size: 13px;
}

.card-caption strong {
  max-width: 75%;
  text-align: right;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #fff;
}

.nav-button,
.close-button,
.lightbox-nav {
  border: 0;
  cursor: pointer;
}

.nav-button {
  position: absolute;
  top: 50%;
  z-index: 4;
  display: grid;
  place-items: center;
  width: 86px;
  height: 86px;
  border-radius: 50%;
  background: #fff;
  color: #000;
  font-size: 58px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  transform: translateY(-50%);
  transition: transform 160ms ease, box-shadow 160ms ease;
}

.nav-button:hover {
  box-shadow: 0 14px 40px rgba(0, 0, 0, 0.26);
}

.nav-button:active {
  transform: translateY(-50%) scale(0.94);
}

.nav-button--left {
  left: 48px;
}

.nav-button--right {
  right: 48px;
}

.file-card {
  display: grid;
  place-items: center;
  gap: 14px;
  width: min(360px, 60vw);
  height: 100%;
  padding: 28px;
  text-align: center;
  background: #171717;
  color: #eee;
}

.file-card .q-icon {
  font-size: 58px;
}

.state-panel {
  display: grid;
  place-items: center;
  width: 100%;
  height: 100%;
  color: #333;
  font-size: 18px;
}

.lightbox {
  position: fixed;
  inset: 0;
  z-index: 10000;
  display: grid;
  place-items: center;
  padding: 22px;
  background: rgba(0, 0, 0, 0.9);
}

.lightbox-content {
  position: relative;
  display: grid;
  place-items: center;
  width: min(1180px, 94vw);
  height: min(820px, 88vh);
}

.lightbox-content img,
.lightbox-content video {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.file-card--large {
  width: 100%;
  height: 100%;
  background: transparent;
  color: #eee;
}

.close-button,
.lightbox-nav {
  position: absolute;
  z-index: 2;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fff;
  color: #000;
}

.close-button {
  top: 10px;
  right: 10px;
  width: 44px;
  height: 44px;
}

.lightbox-nav {
  top: 50%;
  width: 58px;
  height: 58px;
  font-size: 34px;
  transform: translateY(-50%);
}

.lightbox-nav--prev {
  left: 10px;
}

.lightbox-nav--next {
  right: 10px;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 180ms ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

@keyframes overlayFadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes cardIn {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 980px) {
  .film-stage {
    padding-top: 52px;
  }

  .film-track {
    height: 62vh;
    padding-inline: 26px;
  }

  .nav-button {
    width: 62px;
    height: 62px;
    font-size: 42px;
  }

  .nav-button--left {
    left: 18px;
  }

  .nav-button--right {
    right: 18px;
  }
}

@media (max-width: 620px) {
  .nav-button {
    display: none;
  }
}
</style>
